<script>
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import customParseFormat from 'dayjs/plugin/customParseFormat'
dayjs.extend(utc)
dayjs.extend(customParseFormat)

import apiMachines from '@/api/api-machines.js'
import apiSensors from '@/api/api-sensors.js'

import Table from '../common/SortedTable.vue'

export default {
    name: 'SensorsComponent',

    components: {
        Table
    },
    
    props: {
        grafanaLink: {
            type: String,
            default: ''
        },
        selectedMachines: {
            type: Array,
            default: () => []
        },
        machines: {
            type: Array,
            default: () => []
        },
        languageStrings: {
            type: Object,
            default: () => {}
        },
        tabState: {
            type: Number,
            default: 0
        },
        role: {
            type: Number,
            default: 0
        },
        forceRerender: {
            type: Number,
            default: 0
        }
    },

    emits: ['sensors-select-machine', 'selected-sensor', 'save-tab-state', 'move-sensor'],

    data() {
        return {
            selectedTab: this.tabState,
            sensors: [],
            selectedSensors: [],
            parameterSets: [],
            sensorGrafanaUrl: ''
        }
    },

    computed: {
        headers() {
            let h = []

            h.push(...[
                {
                    id: '',
                    name: '',
                    sort: false
                },
                {
                    id: '',
                    name: '',
                    sort: false
                }
            ])

            if (this.selectedMachines.length === 0) {
                h.push(
                    {
                        id: 'machine',
                        name: this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sensor021 != null ? this.languageStrings.ui.sensor021 : 'Stroj',
                        sort: true
                    }
                )
            }

            h.push(...[
                {
                    id: 'valid-from',
                    name: this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sensor016 != null ? this.languageStrings.ui.sensor016 : 'Platnost od',
                    sort: true,
                    sortType: 'datetime-emptyfirst'
                },
                {
                    id: 'valid-to',
                    name: this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sensor017 != null ? this.languageStrings.ui.sensor017 : 'Platnost do',
                    sort: true,
                    sortType: 'datetime-emptyfirst'
                },
                {
                    id: 'name',
                    name: this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sensor025 != null ? this.languageStrings.ui.sensor025 : 'Název',
                    sort: true
                }
            ])

            if (this.role > 1) {
                h.push(
                    {
                        id: 'serial-number',
                        name: this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sensor013 != null ? this.languageStrings.ui.sensor013 : 'SN',
                        sort: true
                    }
                )
            }

            h.push(
                {
                    id: 'parameter-set',
                    name: this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sensor022 != null ? this.languageStrings.ui.sensor022 : 'Typ',
                    sort: true
                }
            )

            return h
        },
        rows() {
            let r = []

            for (let i = 0; i < this.sensors.length; i++) {
                r.push({
                    id: this.sensors[i].id,
                    selected: this.selectedSensors.includes(this.sensors[i].id),
                    cells: []
                })

                r[i].cells.push(...[
                    {
                        id: 'chart',
                        align: 'center',
                        contenttype: 'html',
                        clickevent: true,
                        content: '<img src="' + require('@/assets/images/bar-chart-2.svg') + '" height="15px" style="cursor: pointer;" />'
                    },
                    {
                        id: 'move',
                        align: 'center',
                        contenttype: 'html',
                        clickevent: this.sensors[i].registerSet != null && (this.sensors[i].registerSet.customerEdit || this.role > 1) ? true : false,
                        content: this.sensors[i].registerSet != null && (this.sensors[i].registerSet.customerEdit || this.role > 1) ? ('<img src="' + require('@/assets/images/move.svg') + '" height="15px" style="cursor: pointer;" />') : ''
                    }
                ])

                if (this.selectedMachines.length === 0) {
                    r[i].cells.push(
                        {
                            id: 'machine',
                            contenttype: 'text',
                            content: this.sensors[i].machine.name
                        }
                    )
                }

                r[i].cells.push(...[
                    {
                        id: 'valid-from',
                        contenttype: 'datetime',
                        content: this.formatDateTime(this.sensors[i].validFrom)
                    },
                    {
                        id: 'valid-to',
                        contenttype: 'datetime',
                        content: this.sensors[i].validTo != null ? this.formatDateTime(this.sensors[i].validTo) : ''
                    },
                    {
                        id: 'name',
                        contenttype: 'text',
                        content: this.sensors[i].name != null ? this.sensors[i].name : ''
                    }
                ])

                if (this.role > 1) {
                    r[i].cells.push(
                        {
                            id: 'serial-number',
                            contenttype: 'text',
                            content: this.sensors[i].serialNumber
                        }
                    )
                }

                r[i].cells.push(
                    {
                        id: 'parameter-set',
                        contenttype: 'text',
                        content: this.sensors[i].registerSet != null ? this.sensors[i].registerSet.parameterSet : ''
                    }
                )
            }

            return r
        }
    },

    watch: {
        selectedMachines: function() {
            this.loadSensors()
        },
        machines: function() {
            this.loadSensors()
        },
        forceRerender: function() {
            this.loadSensors()
        }
    },

    async mounted() {
        if (this.grafanaLink.length === 0) {
            this.changeTab(1)
        }
        else {
            this.changeTab(0)
        }
        await this.loadParameterSets()
        this.loadSensors()
    },

    methods: {
        formatDateTime(date) {
            return date !== null ? dayjs(date + 'Z').utcOffset(0).format('D.M.YYYY H:mm') : ''
        },
        getUnixTimestamp(date) {
            return dayjs(date + 'Z').utcOffset(0).unix()
        },
        formatGrafanaDateTime(date) {
            return dayjs(date + 'Z').utcOffset(0).format('YYYY-MM-DDTHH:mm:ss') + '.000Z'
        },
        replaceUID(link, uid) {
            return link.replaceAll('<uid>', uid)
        },
        async loadParameterSets() {
            this.parameterSets = []
            let result = await apiSensors.getParameterSets()
            if (result.code === 0) {
                this.parameterSets = result.data
            }
        },
        getGrafanaUrlFromParameterSet(name) {
            if (name.length > 0) {
                let set = this.parameterSets.find((e) => e.id === name)
                if (set != null) {
                    return set.grafanaUrl
                }
            }
            return ''
        },
        async loadSensors() {
            this.sensors = []

            if (this.selectedMachines.length > 0) {
                let result = await apiMachines.getSensors(this.selectedMachines[0])
                if (result.code === 0) {
                    result.data.forEach((element, index) => {
                        if (element.registerSet != null) {
                            result.data[index].grafanaUrl = this.getGrafanaUrlFromParameterSet(element.registerSet.parameterSet)
                        }
                        else {
                            result.data[index].grafanaUrl = ''
                        }
                    })

                    this.sensors = result.data
                }
            }
            else {
                for (let i = 0; i < this.machines.length; i++) {
                    let result = await apiMachines.getSensors(this.machines[i].id)
                    if (result.code === 0) {
                        result.data.forEach((element, index) => {
                            if (element.registerSet != null) {
                                result.data[index].grafanaUrl = this.getGrafanaUrlFromParameterSet(element.registerSet.parameterSet)
                            }
                            else {
                                result.data[index].grafanaUrl = ''
                            }
                        })

                        this.sensors.push(...result.data)
                    }
                }
            }

            this.sensors = this.sensors.sort((a, b) => (dayjs(b.validFrom).toDate() - dayjs(a.validFrom).toDate()) || (a.validTo == null ? 1 : b.validTo == null ? -1 : dayjs(b.validTo).toDate() - dayjs(a.validTo).toDate()))
        },
        changeTab(tab) {
            this.selectedTab = tab
            this.$emit('save-tab-state', tab)
        },
        clickEvent(sensorId, cellId) {
            if (cellId === 'chart') {
                if (this.selectedMachines.length === 0) {
                    let s = this.sensors.find(s => s.id === sensorId)
                    this.sensorGrafanaUrl = this.replaceUID(s.grafanaUrl, s.uid) + (s.validTo != null ? '&from=' + this.formatGrafanaDateTime(s.validFrom) + '&to=' + this.formatGrafanaDateTime(s.validTo) : '')
                    this.$emit('sensors-select-machine', s.machine.id)
                    this.changeTab(0)
                }
                else {
                    let s = this.sensors.find(s => s.id === sensorId)
                    this.sensorGrafanaUrl = this.replaceUID(s.grafanaUrl, s.uid) + (s.validTo != null ? '&from=' + this.formatGrafanaDateTime(s.validFrom) + '&to=' + this.formatGrafanaDateTime(s.validTo) : '')
                    this.changeTab(0)
                }
            }
            if (cellId === 'move') {
                this.$emit('move-sensor', this.sensors.find(s => s.id === sensorId))
            }
        },
        setSelectedSensors(value) {
            this.selectedSensors = value
            if (value.length > 0) {
                this.$emit('selected-sensor', this.sensors.find(s => s.id === value[0]))
            }
            else {
                this.$emit('selected-sensor', -1)
            }
        }
    }
}
</script>


<template>
    <div>
        <button :class="'button-small' + (selectedTab === 0 ? ' active' : '')" @click="changeTab(0)">{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.sensor019 != null ? languageStrings.ui.sensor019 : 'Grafy' }}</button>
        <button :class="'button-small' + (selectedTab === 1 ? ' active' : '')" @click="changeTab(1)">{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.sensor020 != null ? languageStrings.ui.sensor020 : 'Administrace' }}</button>

        <div v-if="selectedTab === 0" class="grafana-iframe">            
            <iframe :src="sensorGrafanaUrl.length > 0 ? sensorGrafanaUrl : grafanaLink"></iframe>
        </div>

        <div v-if="selectedTab === 1" class="sensors">
            <Table :headers="headers" :rows="rows" @selected-items="setSelectedSensors" @click-event="clickEvent" />
        </div>
    </div>
</template>